import Api from 'utils/api'

// Resource layer for the restaurant ("Order management") backend, mounted on the
// shared LoyalIdeas server at /api/v1/restaurant/*. Account scoping is handled
// server-side from the JWT (see middleware/account.js), so no account id needs
// to be passed here. Every method returns the { response, error } shape from
// utils/api, where `response` is the raw `{ success, data, pagination }` body.

const withQuery = (path, params) => {
  if (!params) return path
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  ).toString()
  return qs ? `${path}?${qs}` : path
}

export const productsApi = {
  list: (params) => Api(withQuery('/restaurant/products', params), 'get'),
  get: (id) => Api(`/restaurant/products/${id}`, 'get'),
  create: (payload, notify = true) =>
    Api('/restaurant/products', 'post', { payload, notify }),
  update: (id, payload, notify = true) =>
    Api(`/restaurant/products/${id}`, 'put', { payload, notify }),
  remove: (id, notify = true) =>
    Api(`/restaurant/products/${id}`, 'delete', { notify }),
  duplicate: (id, notify = true) =>
    Api(`/restaurant/products/${id}/duplicate`, 'post', { notify }),
  toggleVisibility: (id) =>
    Api(`/restaurant/products/${id}/visibility`, 'patch'),
  toggleAvailability: (id) =>
    Api(`/restaurant/products/${id}/availability`, 'patch'),
}

export const categoriesApi = {
  list: () => Api('/restaurant/categories', 'get'),
  create: (payload, notify = true) =>
    Api('/restaurant/categories', 'post', { payload, notify }),
  update: (id, payload, notify = true) =>
    Api(`/restaurant/categories/${id}`, 'put', { payload, notify }),
  remove: (id, notify = true) =>
    Api(`/restaurant/categories/${id}`, 'delete', { notify }),
}

export const bannersApi = {
  list: () => Api('/restaurant/banners', 'get'),
  create: (payload, notify = true) =>
    Api('/restaurant/banners', 'post', { payload, notify }),
  update: (id, payload, notify = true) =>
    Api(`/restaurant/banners/${id}`, 'put', { payload, notify }),
  remove: (id, notify = true) =>
    Api(`/restaurant/banners/${id}`, 'delete', { notify }),
}

export const dealsApi = {
  list: () => Api('/restaurant/deals', 'get'),
  create: (payload, notify = true) =>
    Api('/restaurant/deals', 'post', { payload, notify }),
  update: (id, payload, notify = true) =>
    Api(`/restaurant/deals/${id}`, 'put', { payload, notify }),
  remove: (id, notify = true) =>
    Api(`/restaurant/deals/${id}`, 'delete', { notify }),
}

// Coupons are mounted separately at /restaurant/coupons (not under /deals).
export const couponsApi = {
  list: () => Api('/restaurant/coupons', 'get'),
  create: (payload, notify = true) =>
    Api('/restaurant/coupons', 'post', { payload, notify }),
  update: (id, payload, notify = true) =>
    Api(`/restaurant/coupons/${id}`, 'put', { payload, notify }),
  remove: (id, notify = true) =>
    Api(`/restaurant/coupons/${id}`, 'delete', { notify }),
}

export const ordersApi = {
  list: (params) => Api(withQuery('/restaurant/orders', params), 'get'),
  get: (id) => Api(`/restaurant/orders/${id}`, 'get'),
  // Backend uses PUT /:id/status (not PATCH).
  updateStatus: (id, status, notify = true) =>
    Api(`/restaurant/orders/${id}/status`, 'put', {
      payload: { status },
      notify,
    }),
  remove: (id, notify = true) =>
    Api(`/restaurant/orders/${id}`, 'delete', { notify }),
}

export const branchesApi = {
  list: () => Api('/restaurant/branches', 'get'),
  create: (payload, notify = true) =>
    Api('/restaurant/branches', 'post', { payload, notify }),
  update: (id, payload, notify = true) =>
    Api(`/restaurant/branches/${id}`, 'put', { payload, notify }),
  remove: (id, notify = true) =>
    Api(`/restaurant/branches/${id}`, 'delete', { notify }),
}

export const settingsApi = {
  get: () => Api('/restaurant/settings', 'get'),
  update: (payload, notify = true) =>
    Api('/restaurant/settings', 'put', { payload, notify }),
}

// Analytics is mounted at /analytics (not under /restaurant).
// `reports` accepts a params object (e.g. { period, scope, slug }) and
// serializes it into query string parameters. This allows requesting
// website-scoped reports (by slug) instead of the platform-wide defaults.
export const analyticsApi = {
  dashboard: () => Api('/analytics/dashboard', 'get'),
  reports: (params) => Api(withQuery('/analytics/reports', params), 'get'),
}
